import React, { useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
} from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import TextInputComponent from '../../components/textinput/TextInputComponent';
import ajax from '../../helpers/ajaxHelper';
import Urls from '../../services/Urls';
import { PasswordComparisonValidation, PasswordValidation } from '../../helpers/validation';
import { navigate } from '../../navigation/RootNavigation';
import { showErrorToast, showSuccesToast } from '../tenants/tenantOnboarding/helpers';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';

const ChangePassword = () => {
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirm_pass, setConfirmPass] = useState('');

  const [passError, setPassError] = useState('');
  const [currentError, setCurrentPassError] = useState('');
  const [confirmPassError, setConfirmPassError] = useState('');
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setCurrentPassword('');
    setPassword('');
    setConfirmPass('');
    setPassError('');
    setCurrentPassError('');
    setConfirmPassError('');
  };

  const _onPasswordChange = () => {
    let CurrentPasswordValidate = PasswordValidation(currentPassword);
    let PasswordValidate = PasswordValidation(password);
    let ConfirmPassValidate = PasswordComparisonValidation(password, confirm_pass);

    if (CurrentPasswordValidate.status && PasswordValidate.status && ConfirmPassValidate.status) {
      setLoading(true);
      const payload = { old_password: currentPassword, new_password: password };
      ajax
        .post(Urls.CHANGE_PASSWORD, payload)
        .then(({ data }) => {
          setLoading(false);
          if (data.status) {
            resetForm();
            showSuccesToast(data.data || 'Password changed successfully!');
            navigate('Setting');
          } else {
            showErrorToast(data.message || 'Failed to change password');
          }
        })
        .catch((error) => {
          setLoading(false);
          console.log('error change password', error);
          const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
          showErrorToast(errorMessage);
        });
    } else {
      setPassError(PasswordValidate?.status ? '' : PasswordValidate.msg);
      setCurrentPassError(CurrentPasswordValidate?.status ? '' : CurrentPasswordValidate.msg);
      setConfirmPassError(ConfirmPassValidate?.status ? '' : ConfirmPassValidate.msg);
    }
  };

  // Helper for password strength indicator
  const getPasswordStrength = () => {
    if (!password) return { text: 'Enter a new password', color: '#9CA3AF', fill: '0%' };
    if (password.length < 6) return { text: 'Weak', color: '#EF4444', fill: '33%' };
    if (password.length < 10) return { text: 'Medium', color: '#F59E0B', fill: '66%' };
    return { text: 'Strong', color: '#10B981', fill: '100%' };
  };

  const strength = getPasswordStrength();

  return (
    <ScrollView style={[styles.container, isMobile && styles.containerMobile]} showsVerticalScrollIndicator={false}>
      {/* Breadcrumb Navigation */}
      <Text style={styles.breadcrumbText}>Settings › Security › Change Password</Text>

      <View style={styles.headerRow}>
        <Text style={styles.pageTitle}>Change Password</Text>
      </View>

      {/* Main 2-Column Grid matching Figma Node 448-270 / 450-182 */}
      <View style={[styles.gridRow, isMobile && styles.gridRowMobile]}>
        {/* Left Column: Form Card */}
        <View style={[styles.cardContainer, isMobile && styles.cardContainerMobile]}>
          <Text style={styles.sectionTitle}>Update Security Password</Text>

          {/* Current Password */}
          <View style={styles.fieldGroup}>
            <TextInputComponent
              isDate={false}
              error={currentError}
              password
              renderRightComponent
              value={currentPassword}
              title={'Current Password *'}
              placeholder="Enter current password"
              onChangeText={(e) => {
                setCurrentPassword(e);
                setCurrentPassError('');
              }}
              height={44}
            />
          </View>

          {/* New Password */}
          <View style={styles.fieldGroup}>
            <TextInputComponent
              isDate={false}
              error={passError}
              password
              renderRightComponent
              value={password}
              title={'New Password *'}
              placeholder="Enter new password"
              onChangeText={(e) => {
                setPassword(e);
                setPassError('');
                setConfirmPassError('');
              }}
              height={44}
            />

            {/* Password Strength Indicator matching Figma */}
            <View style={styles.strengthWrapper}>
              <View style={styles.strengthBarTrack}>
                <View style={[styles.strengthBarFill, { width: strength.fill, backgroundColor: strength.color }]} />
              </View>
              <Text style={styles.strengthText}>Password strength: {strength.text}</Text>
            </View>
          </View>

          {/* Confirm New Password */}
          <View style={styles.fieldGroup}>
            <TextInputComponent
              isDate={false}
              error={confirmPassError}
              password
              renderRightComponent
              value={confirm_pass}
              title={'Confirm New Password *'}
              placeholder="Confirm new password"
              onChangeText={(e) => {
                setConfirmPass(e);
                setConfirmPassError('');
              }}
              height={44}
            />
          </View>

          {/* Apply Changes Button */}
          <TouchableOpacity
            style={[styles.applyBtn, loading && styles.applyBtnDisabled]}
            onPress={_onPasswordChange}
            disabled={loading}
            activeOpacity={0.85}
          >
            <Text style={styles.applyBtnText}>
              {loading ? 'Applying...' : 'Apply Changes'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Right Column: Password Requirements Card */}
        <View style={[styles.reqCard, isMobile && styles.reqCardMobile]}>
          <Text style={styles.reqTitle}>Password Requirements</Text>

          <View style={styles.reqItem}>
            <Feather name="check" size={14} color="#6C2BD9" style={{ marginRight: 8, marginTop: 2 }} />
            <Text style={styles.reqText}>At least 12 characters long (longer is safer)</Text>
          </View>

          <View style={styles.reqItem}>
            <Feather name="check" size={14} color="#6C2BD9" style={{ marginRight: 8, marginTop: 2 }} />
            <Text style={styles.reqText}>Mix of uppercase &amp; lowercase alphabetical characters</Text>
          </View>

          <View style={styles.reqItem}>
            <Feather name="check" size={14} color="#6C2BD9" style={{ marginRight: 8, marginTop: 2 }} />
            <Text style={styles.reqText}>Include at least one digit (0-9)</Text>
          </View>

          <View style={styles.reqItem}>
            <Feather name="check" size={14} color="#6C2BD9" style={{ marginRight: 8, marginTop: 2 }} />
            <Text style={styles.reqText}>Include at least one symbol (e.g. ! @ # $ %)</Text>
          </View>

          <View style={styles.reqItem}>
            <Feather name="check" size={14} color="#6C2BD9" style={{ marginRight: 8, marginTop: 2 }} />
            <Text style={styles.reqText}>Avoid common passwords, generic patterns or personal names</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default drawerHoc({
  Component: ChangePassword,
  showBackButton: true,
  backNavigation: 'Setting',
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    marginBottom: 18,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
  },

  gridRow: {
    flexDirection: 'row',
    gap: 24,
    alignItems: 'flex-start',
    marginBottom: 40,
  },
  gridRowMobile: {
    flexDirection: 'column',
    gap: 16,
  },

  /* FORM CARD */
  cardContainer: {
    flex: 1,
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 24,
    ...SHADOWS.sm,
  },
  cardContainerMobile: {
    padding: 16,
    borderRadius: BORDER_RADIUS.md,
    width: '100%',
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 18,
  },

  fieldGroup: {
    marginBottom: 16,
  },

  strengthWrapper: {
    marginTop: 8,
  },
  strengthBarTrack: {
    height: 4,
    backgroundColor: '#F3F4F6',
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: 4,
  },
  strengthBarFill: {
    height: '100%',
  },
  strengthText: {
    fontSize: 11,
    color: '#6B7280',
  },

  applyBtn: {
    backgroundColor: '#6C2BD9',
    borderRadius: BORDER_RADIUS.md,
    height: 42,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  applyBtnDisabled: {
    backgroundColor: '#9CA3AF',
  },
  applyBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FFFFFF',
  },

  /* REQUIREMENTS CARD */
  reqCard: {
    width: 320,
    backgroundColor: '#F5F3FF',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: '#E0E7FF',
    padding: 20,
  },
  reqCardMobile: {
    width: '100%',
  },

  reqTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#6C2BD9',
    marginBottom: 14,
  },

  reqItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 10,
  },
  reqText: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
    flex: 1,
  },
});
